const env = (import.meta as ImportMeta & {
  env?: { DEV?: boolean; VITE_API_URL?: string; VITE_WEBAPP?: string };
}).env;
const configured = env?.VITE_API_URL?.replace(/\/$/, '');
const BASE = env?.DEV
  ? ''
  : configured || (env?.VITE_WEBAPP === 'true' ? '' : 'http://localhost:8080');

export type ApiResult<T = any> = {
  success: boolean;
  data?: T;
  error?: string;
};

export async function api<T = any>(path: string, options: RequestInit = {}): Promise<ApiResult<T>> {
  const token = localStorage.getItem('dms_token');
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    const text = await res.text();
    let body: any = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = { message: text };
    }
    if (!res.ok) {
      const serverMessage = typeof body?.message === 'string' ? body.message.trim() : '';
      return { success: false, error: friendlyError(res.status, serverMessage) };
    }
    return { success: true, data: body as T };
  } catch {
    return {
      success: false,
      error: 'The plant server is not reachable. Check that it is running, then try again.',
    };
  }
}

function friendlyError(status: number, serverMessage: string): string {
  if (serverMessage && !/exception|sql|hibernate|constraint/i.test(serverMessage)) {
    return serverMessage;
  }
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'You do not have permission to do this.';
  if (status === 404) return 'That record was not found. Refresh the page and try again.';
  if (status === 400) return serverMessage || 'Some details are missing or not valid. Check the form and try again.';
  return 'Something went wrong while saving. Try again. If it keeps happening, contact the plant administrator.';
}

export async function apiText(path: string): Promise<ApiResult<string>> {
  const token = localStorage.getItem('dms_token');
  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const text = await res.text();
    if (!res.ok) {
      let message = '';
      try {
        const body = JSON.parse(text);
        message = typeof body?.message === 'string' ? body.message : '';
      } catch {
        message = '';
      }
      return { success: false, error: message || 'Could not open the invoice.' };
    }
    return { success: true, data: text };
  } catch {
    return {
      success: false,
      error: 'The plant server is not reachable. Check that it is running, then try again.',
    };
  }
}

export async function apiBlob(path: string): Promise<ApiResult<Blob> & { filename?: string }> {
  const token = localStorage.getItem('dms_token');
  try {
    const res = await fetch(`${BASE}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const text = await res.text();
      let message = '';
      try {
        const body = JSON.parse(text);
        message = typeof body?.message === 'string' ? body.message : '';
      } catch {
        message = '';
      }
      return { success: false, error: friendlyError(res.status, message) };
    }
    const blob = await res.blob();
    const header = res.headers.get('Content-Disposition') || '';
    const match = /filename="([^"]+)"/.exec(header);
    return { success: true, data: blob, filename: match?.[1] };
  } catch {
    return {
      success: false,
      error: 'The plant server is not reachable. Check that it is running, then try again.',
    };
  }
}

export function qs(params: Record<string, string | number | boolean | undefined | null>) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') q.set(key, String(value));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}
